// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file TopNavItem.tsx
 * @input Uses React, ReactNode, BaseProps, LinkComponentType
 * @output Exports TopNavItem component and TopNavItemProps
 * @position Navigation item component for TopNav startContent
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/TopNav/TopNav.doc.mjs
 * - /packages/core/src/TopNav/TopNav.test.tsx
 * - /packages/core/src/TopNav/index.ts
 * - /apps/storybook/stories/TopNav.stories.tsx
 * - /packages/cli/templates/blocks/components/TopNav/ (showcase blocks)
 */
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, durationVars, easeVars, fontWeightVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { useTopNavRenderMode } from "./TopNavRenderContext.js";
import { navItemStyles } from "../NavItem/navItemStyles.stylex.js";
import { mergeProps } from "../utils/index.js";
import { useAppShellMobile } from "../AppShell/AppShellMobileContext.js";
import { themeProps } from "../utils/themeProps.js";

/**
 * NavItem styles with hover/selected states
 */
import { jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  base: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    k8WAf4: "x1vofgu7",
    kg3NbH: "xrrkdod",
    kaIpWk: "xh6dtrn",
    kGuDYH: "xcr08ib",
    kLWn49: "x1kq96og",
    k63SB2: "x1e4wzip",
    kMwMTN: "xv1l7n4",
    kybGjl: "x1hl2dhg",
    kkrTdU: "x1ypdohk",
    k1ekBW: "xs2xxs2",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    kWkggS: "xjbqb8w xe9uy6x xyxi2l3",
    kI3sdo: "x17nn4n9",
    kInvED: "x1wfwxd8 x7s97pk",
    $$css: true
  },
  selected: {
    kMwMTN: "x1tgivj0",
    k63SB2: "x2mo6ok",
    kWkggS: "x17x4s8c x1sua13m xjtnuge",
    $$css: true
  },
  iconOnly: {
    kg3NbH: "xf314gf",
    $$css: true
  },
  drawerFocus: {
    kI3sdo: "x17nn4n9",
    kInvED: "x1wfwxd8 x7s97pk",
    $$css: true
  }
};
/**
 * A navigation item for use within TopNav startContent.
 *
 * Renders as an anchor element with hover/selected states.
 * Supports icons and selected state indication with highlighted appearance.
 *
 * @example
 * ```
 * <TopNav
 *   startContent={
 *     <>
 *       <TopNavItem label="Home" href="/" isSelected />
 *       <TopNavItem label="Products" href="/products" />
 *       <TopNavItem label="Settings" href="/settings" icon={<GearIcon />} isIconOnly />
 *     </>
 *   }
 * />
 * ```
 */
export function TopNavItem({
  as,
  label,
  isSelected = false,
  isDisabled = false,
  isIconOnly = false,
  icon,
  children,
  size = 'md',
  xstyle,
  className,
  style,
  ref,
  ...props
}) {
  const LinkComponent = useLinkComponent(as);
  const renderMode = useTopNavRenderMode();
  const {
    closeMobileNav
  } = useAppShellMobile();

  // =========================================================================
  // Drawer mode — render as a SideNavItem-style vertical list element
  // =========================================================================
  if (renderMode === 'drawer') {
    const handleDrawerClick = e => {
      if (isDisabled) {
        return;
      }
      // Forward the original onClick if present
      props.onClick?.(e);
      closeMobileNav();
    };
    return /*#__PURE__*/_jsxs(LinkComponent, {
      ref: ref,
      "aria-label": isIconOnly ? label : undefined,
      "aria-current": isSelected ? 'page' : undefined,
      "aria-disabled": isDisabled || undefined,
      tabIndex: isDisabled ? -1 : undefined,
      ...mergeProps(themeProps('top-nav-item', {
        mode: 'drawer',
        selected: isSelected ? 'selected' : null
      }), stylex.props(navItemStyles.item, navItemStyles[size], styles.drawerFocus, isSelected && navItemStyles.selected, isDisabled && navItemStyles.disabled, xstyle), className, style),
      ...props,
      onClick: handleDrawerClick,
      children: [icon, !isIconOnly && (children ?? label)]
    });
  }

  // =========================================================================
  // Default / mobile-bar mode — standard horizontal nav item
  // =========================================================================

  return /*#__PURE__*/_jsxs(LinkComponent, {
    ref: ref,
    "aria-label": isIconOnly ? label : undefined,
    "aria-current": isSelected ? 'page' : undefined,
    "aria-disabled": isDisabled || undefined,
    tabIndex: isDisabled ? -1 : undefined,
    ...mergeProps(themeProps('top-nav-item', {
      selected: isSelected ? 'selected' : null
    }), stylex.props(styles.base, isSelected && styles.selected, isDisabled && navItemStyles.disabled, isIconOnly && styles.iconOnly, xstyle), className, style),
    ...props,
    children: [icon, !isIconOnly && (children ?? label)]
  });
}
TopNavItem.displayName = 'TopNavItem';